import { html, customElement, classMap } from '@adobe/edex/ui/edex-lit';
import { DialogHeadingClassName, EdexTagName } from '@adobe/edex/ui/shared/constants';
import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { BaseElement } from '@adobe/edex/ui/components/base.element';
import { Overlay } from '@spectrum-web-components/overlay';
import { getAssetURL } from '@adobe/edex/ui/shared/utils';
import { isEmpty } from 'lodash';
import '@spectrum-web-components/checkbox/sp-checkbox.js';
import '@spectrum-web-components/dialog/sp-dialog-wrapper.js';
import '@spectrum-web-components/textfield/sp-textfield.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';

import(
  /* webpackChunkName: "component/resource-delete-dialog-styles" */
  /* webpackPreload: true */
  /* webpackMode: "lazy" */
  './import-details-dialog.scss'
);

interface MemberDetails {
  memberName: string;
  email: string;
}
const statusToIconMap = Object.freeze({
  success: 'success.svg',
  failure: 'error.svg',
  ignored: 'info.svg',
});
@customElement(EdexTagName.importDetailsDialog)
export class Dialog extends BaseElement {
  static get properties() {
    return {
      dismissable: { type: Boolean },
      divider: { type: Boolean },
      allSelected: { type: Boolean },
      headingClassName: { type: String },
      enrollmentDetails: { type: String },
      enrollmentRows: { type: Object },
      selectedStudents: { type: Object },
      selectedCount: { type: Number },
      reportUrl: { type: String },
      markAsPassed: { type: String },
      updateInProgress: { type: Boolean },
    };
  }
  dismissable = false;
  divider = false;
  headingClassName = DialogHeadingClassName.default;
  closeOverlay: any;
  enrollmentDetails = '';
  enrollmentRows = [];
  selectedStudents = new Set<string>();
  memberIdToMemberNameMapping = new Map<string, MemberDetails>();
  allSelected = false;
  selectedCount = 0;
  reportUrl = '';
  markAsPassed = false;
  reviewComment = '';
  updateInProgress = false;

  areAllSelected() {
    const successfulEnrollments = this.enrollmentRows.filter((enrollment) => this.isNotFailure(enrollment));
    return successfulEnrollments.length !== 0 && successfulEnrollments.length === this.selectedStudents.size;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('sp-closed', this.emitCloseEvent);
  }

  updateMemberIdToMemberNameMapping() {
    this.enrollmentRows.forEach((enrollmentRow) => {
      this.memberIdToMemberNameMapping.set(enrollmentRow.memberId, {
        memberName: enrollmentRow.memberName,
        email: enrollmentRow.email,
      });
    });
  }

  disconnectedCallback() {
    this.closeOverlay?.();
    this.removeEventListener('sp-closed', this.emitCloseEvent);
    super.disconnectedCallback();
  }

  downloadEnrollmentReport(reportUrl) {
    const fileLink = document.getElementById('downloadReportHiddenAnchor') as HTMLAnchorElement;
    fileLink.href = reportUrl;
    fileLink.click();
  }

  async firstUpdated() {
    await this.openOverlay();

    this.enrollmentRows.forEach((enrollmentRow) => {
      this.addToSelected(enrollmentRow);
    });
    this.allSelected = this.areAllSelected();
    this.updateSelectedCount();

    if (!isEmpty(this.reportUrl)) {
      this.downloadEnrollmentReport(this.reportUrl);
    }
  }

  openOverlay = async () => {
    const interaction = 'modal';
    const content = this.shadowRoot.querySelector('sp-dialog-wrapper') as any;

    this.shadowRoot.querySelectorAll('slot').forEach((slot) => {
      const nodes = [...slot.assignedNodes({ flatten: true })];
      if (nodes.length) {
        content.querySelector(':first-child').appendChild(...nodes);
      }
    });

    const options = {
      offset: 0,
      placement: 'none',
      receivesFocus: 'auto',
    };
    this.closeOverlay = await Overlay.open(this, interaction, content, options as any);
  };

  emitCloseEvent = () => {
    this.emitEvent('close');
  };

  addToSelected(enrollment) {
    //add to selected only if student enrollment status is not failure.
    if (this.isNotFailure(enrollment)) {
      this.selectedStudents.add(enrollment.memberId);
    }
  }

  removeFromSelected(enrollment) {
    this.selectedStudents.delete(enrollment.memberId);
  }

  handleSelectAll(event: Event) {
    const { checked } = event.target as HTMLInputElement;
    this.enrollmentRows.forEach((enrollmentRow) => {
      if (checked) {
        this.addToSelected(enrollmentRow);
      } else {
        this.removeFromSelected(enrollmentRow);
      }
    });

    this.updateSelectedCount();
  }

  handleMarkAsPassed() {
    this.markAsPassed = !this.markAsPassed;
  }

  handleSubmitClick() {
    if (this.markAsPassed) {
      this.updateMemberIdToMemberNameMapping();
      const members = [];
      this.selectedStudents.forEach((memberId) => {
        const member = this.memberIdToMemberNameMapping.get(memberId);
        members.push({
          memberID: memberId,
          memberName: member?.memberName,
          email: member?.email,
        });
      });
      this.updateInProgress = true;
      this.emitEvent('markEnrollment', {
        members,
        status: 'passed',
        reviewComment: this.reviewComment,
      });
    }
  }

  updateSelectedCount() {
    this.selectedCount = this.selectedStudents.size;
    this.allSelected = this.areAllSelected();
  }

  handleRowCbChange(enrollment, event: Event) {
    const { checked } = event.target as HTMLInputElement;

    if (checked) {
      this.addToSelected(enrollment);
    } else {
      this.removeFromSelected(enrollment);
    }
    this.updateSelectedCount();
  }

  isNotFailure(enrollment) {
    return enrollment.status !== 'failure';
  }

  isSelected(memberId: string) {
    return this.selectedStudents.has(memberId);
  }

  renderDownloadedInfo() {
    return !isEmpty(this.reportUrl)
      ? html`
          <p class="report-downloaded-info">Report is downloaded</p>
        `
      : null;
  }

  getIconImageName(status: string) {
    return statusToIconMap[status];
  }

  renderTable() {
    return html`
      <table class="col-md-48" v-if="enrollees" aria-describedby="course-enrollees__h1" cellspacing="0" cellpadding="0">
        <thead>
          <tr class="header-row">
            <th class="pointer align-right" scope="col">
              <sp-checkbox size="m" ?checked=${this.allSelected} @change=${this.handleSelectAll} />
            </th>
            <th class="table-header align-left" scope="col">${$t('i18n.Student')}</th>
            <th class="table-header align-left right" scope="col">
              ${$t('i18n.EnrollmentStatus')}
              <span class="download-info">${this.renderDownloadedInfo()}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          ${this.enrollmentRows.map((enrollmentRow) => {
            return html`
              <tr
                class="${classMap({
                  selected: this.isSelected(enrollmentRow.memberId),
                })}"
              >
                <td class="align-right">
                  <sp-checkbox
                    size="m"
                    ?checked=${this.isNotFailure(enrollmentRow) && this.isSelected(enrollmentRow.memberId)}
                    ?disabled=${!this.isNotFailure(enrollmentRow)}
                    @change=${(event) => this.handleRowCbChange(enrollmentRow, event)}
                  />
                </td>
                <td class="align-left">${enrollmentRow.memberName}</td>
                <td class="align-left right">
                  <img
                    class="status-icon ${classMap({
                      'error-icon': enrollmentRow.status === 'failure',
                      'success-icon': enrollmentRow.status === 'success',
                      'info-icon': enrollmentRow.status === 'ignored',
                    })}"
                    src="${getAssetURL(this.getIconImageName(enrollmentRow.status))}"
                  />
                  ${enrollmentRow.message}
                </td>
              </tr>
            `;
          })}
        </tbody>
      </table>
    `;
  }

  render() {
    const { dismissable, divider, headingClassName, enrollmentDetails } = this;
    const enrollmentResult = JSON.parse(enrollmentDetails);
    this.enrollmentRows = enrollmentResult.enrollmentData;
    return html`
      <overlay-trigger>
        <sp-dialog-wrapper
          underlay
          ?dismissable=${dismissable}
          ?no-divider=${!divider}
          ?responsive=${true}
          @close=${this.emitCloseEvent}
          headingClass=${headingClassName}
          .divider=${false}
        >
          <div class="import-dialog">
            <table class="no-decoration">
              <tr>
                <td>
                  <div>
                    <h2 class="import-header-title">
                      Selecting
                      ${this.selectedCount}/${this.enrollmentRows.length}
                      students
                    </h2>
                  </div>
                </td>
                <td>
                  <div>
                    <sp-checkbox
                      ?checked=${this.markAsPassed}
                      @change=${this.handleMarkAsPassed}
                      size="m"
                    >
                      Mark as passed
                    </sp-checkbox>
                  </div>
                </td>
                <td>
                  <div>
                    <sp-textfield placeholder="Comment" @change=${(e) =>
                      (this.reviewComment = e.target.value)}></sp-textfield>
                  </div>
                </td>
                <td>
                  <div>
                    <sp-button @click=${this.handleSubmitClick} ?disabled=${this.updateInProgress}>
                      Submit
                    </sp-button>
                  </div>
                </td>
                <td ?hidden=${!this.updateInProgress}>
                  <div>
                    <sp-progress-circle
                      label="In progress"
                      indeterminate
                      size="s"
                    ></<sp-progress-circle>
                  </div>
                </td>
              </tr>
            </table>
            <a id="downloadReportHiddenAnchor" href="" hidden></a>
            <div>${this.renderTable()}</div>
          </div>
        </sp-dialog-wrapper>
      </overlay-trigger>
    `;
  }
}
